// OrdersFulfillmentView.jsx — /shop/orders
// Staff-only list of paid shop orders mirrored from Square by the
// squareOrderWebhook. Grouped by fulfillment status. Operator taps through
// Packed → Shipped (with tracking) → Complete. The shipping-notification
// email fires automatically from a Firestore trigger (onShopOrderShipped)
// the moment trackingNumber lands on the doc.

import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { STAFF_THEME, FONT } from "../../theme/staff";

const B = STAFF_THEME;

// Ordering + labels for the four fulfillment states
const GROUPS = [
  { key: "pending",  label: "Pending",  emoji: "📦", blurb: "Paid — waiting to be packed." },
  { key: "packed",   label: "Packed",   emoji: "✅", blurb: "Boxed up — needs a tracking number + ship." },
  { key: "shipped",  label: "Shipped",  emoji: "🚚", blurb: "In the mail. Mark complete when confirmed delivered." },
  { key: "complete", label: "Complete", emoji: "🎉", blurb: "Done and dusted." },
];

const CARRIERS = [
  { value: "usps",   label: "USPS" },
  { value: "ups",    label: "UPS" },
  { value: "fedex",  label: "FedEx" },
  { value: "dhl",    label: "DHL" },
  { value: "other",  label: "Other" },
];

export default function OrdersFulfillmentView({ navigate }) {
  const [orders, setOrders] = useState(null);   // null = loading
  const [expanded, setExpanded] = useState({ pending: true, packed: true, shipped: false, complete: false });
  const [trackingModal, setTrackingModal] = useState(null); // order being shipped

  useEffect(() => {
    const q = query(collection(db, "shopOrders"), orderBy("paidAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error("Shop orders subscribe failed", err);
      setOrders([]);
    });
    return () => unsub();
  }, []);

  const grouped = useMemo(() => {
    const g = { pending: [], packed: [], shipped: [], complete: [] };
    (orders || []).forEach((o) => {
      const key = o.fulfillmentStatus || "pending";
      if (g[key]) g[key].push(o);
    });
    return g;
  }, [orders]);

  async function markPacked(order) {
    await updateDoc(doc(db, "shopOrders", order.id), {
      fulfillmentStatus: "packed",
      packedAt:          serverTimestamp(),
      updatedAt:         serverTimestamp(),
    });
  }

  async function markShipped(order, { trackingNumber, trackingCarrier }) {
    await updateDoc(doc(db, "shopOrders", order.id), {
      fulfillmentStatus: "shipped",
      trackingNumber:    trackingNumber.trim(),
      trackingCarrier:   trackingCarrier,
      shippedAt:         serverTimestamp(),
      updatedAt:         serverTimestamp(),
    });
    // The onShopOrderShipped Firestore trigger will send the customer email
    // once trackingNumber lands on the doc — no client-side email call needed.
  }

  async function markComplete(order) {
    await updateDoc(doc(db, "shopOrders", order.id), {
      fulfillmentStatus: "complete",
      completedAt:       serverTimestamp(),
      updatedAt:         serverTimestamp(),
    });
  }

  return (
    <div style={{ minHeight: "100vh", background: B.dark, color: "white", fontFamily: FONT.body }}>
      {/* Top bar */}
      <div style={{ padding: "0.75rem 1rem", background: B.panel, borderBottom: `1px solid ${B.border}`, display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ color: B.gold, fontWeight: "bold", fontSize: "1rem", fontFamily: FONT.display }}>📦 Shop Orders</div>
        <div style={{ color: B.muted, fontSize: "0.78rem" }}>
          {orders === null ? "Loading…" : `${orders.length} total`}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate("/menu")} style={{ background: "transparent", border: `1px solid ${B.border}`, borderRadius: "8px", color: B.muted, fontSize: "0.78rem", fontWeight: 600, padding: "0.35rem 0.7rem", cursor: "pointer" }}>
          ← Menu
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: "1rem", maxWidth: "920px", margin: "0 auto" }}>
        {orders === null ? (
          <Loading />
        ) : orders.length === 0 ? (
          <EmptyState />
        ) : (
          GROUPS.map((g) => (
            <GroupSection
              key={g.key}
              group={g}
              orders={grouped[g.key]}
              expanded={expanded[g.key]}
              onToggle={() => setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))}
              onMarkPacked={markPacked}
              onOpenShipModal={(o) => setTrackingModal(o)}
              onMarkComplete={markComplete}
            />
          ))
        )}
      </div>

      {trackingModal && (
        <TrackingModal
          order={trackingModal}
          onClose={() => setTrackingModal(null)}
          onConfirm={async (fields) => {
            await markShipped(trackingModal, fields);
            setTrackingModal(null);
          }}
        />
      )}
    </div>
  );
}

// ── Sections ──────────────────────────────────────────────────────────────

function GroupSection({ group, orders, expanded, onToggle, onMarkPacked, onOpenShipModal, onMarkComplete }) {
  const count = orders.length;
  return (
    <div style={{ marginBottom: "1.25rem", background: B.panel, border: `1px solid ${B.border}`, borderRadius: "12px", overflow: "hidden" }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: "0.75rem",
          padding: "0.85rem 1rem", background: "none", border: "none", cursor: "pointer",
          textAlign: "left", color: "inherit", fontFamily: "inherit",
        }}
      >
        <span style={{ fontSize: "1.2rem" }}>{group.emoji}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: B.cream, fontWeight: 800, fontFamily: FONT.display, fontSize: "1rem" }}>
            {group.label} <span style={{ color: B.gold, marginLeft: "0.35rem" }}>{count}</span>
          </div>
          <div style={{ color: B.muted, fontSize: "0.75rem" }}>{group.blurb}</div>
        </div>
        <span style={{ color: B.muted, fontSize: "0.85rem" }}>{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && count > 0 && (
        <div style={{ borderTop: `1px solid ${B.divider}`, padding: "0.75rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              onMarkPacked={onMarkPacked}
              onOpenShipModal={onOpenShipModal}
              onMarkComplete={onMarkComplete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Order card ────────────────────────────────────────────────────────────

function OrderCard({ order, onMarkPacked, onOpenShipModal, onMarkComplete }) {
  const [busy, setBusy] = useState(false);
  const addr = order.shippingAddress || {};
  const total = formatCents(order.totalCents);
  const items = order.lineItems || [];
  const status = order.fulfillmentStatus || "pending";

  async function wrap(fn) {
    setBusy(true);
    try { await fn(order); } catch (e) { alert(e.message || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <div style={{
      background: B.card,
      border: `1px solid ${B.border}`,
      borderRadius: "10px",
      padding: "0.85rem 1rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.6rem",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.6rem", flexWrap: "wrap" }}>
        <div style={{ color: "white", fontWeight: 700, fontSize: "0.95rem" }}>{order.customerName || "(no name)"}</div>
        {order.customerEmail && (
          <a href={`mailto:${order.customerEmail}`} style={{ color: B.sky, fontSize: "0.8rem", textDecoration: "none" }}>{order.customerEmail}</a>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ color: B.gold, fontWeight: 800, fontSize: "1rem" }}>{total}</div>
      </div>

      {/* Meta row */}
      <div style={{ display: "flex", gap: "0.8rem", flexWrap: "wrap", color: B.muted, fontSize: "0.75rem" }}>
        {addr.city && (
          <span>📍 {[addr.city, addr.state, addr.country].filter(Boolean).join(", ")}</span>
        )}
        {order.paidAt && <span>💵 {formatTimestamp(order.paidAt)}</span>}
        {order.squareReceiptUrl && (
          <a href={order.squareReceiptUrl} target="_blank" rel="noopener noreferrer" style={{ color: B.sky, textDecoration: "none" }}>
            Square receipt ↗
          </a>
        )}
      </div>

      {/* Line items */}
      <div style={{ borderTop: `1px solid ${B.divider}`, paddingTop: "0.55rem", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        {items.map((li, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", fontSize: "0.85rem" }}>
            <div style={{ color: "white", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <span style={{ color: B.cream, fontWeight: 700 }}>{li.quantity}× </span>
              {li.name}
              {li.variantName && <span style={{ color: B.muted, marginLeft: "0.35rem" }}>· {li.variantName}</span>}
            </div>
            <div style={{ color: B.muted, flexShrink: 0 }}>{formatCents(li.totalCents)}</div>
          </div>
        ))}
      </div>

      {/* Shipping address */}
      {addr.line1 && (
        <div style={{ background: B.raised, border: `1px solid ${B.divider}`, borderRadius: "8px", padding: "0.55rem 0.75rem", fontSize: "0.8rem", color: B.cream, lineHeight: 1.4 }}>
          <div style={{ color: B.muted, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.2rem" }}>
            Ship to
          </div>
          {addr.line1}
          {addr.line2 && <>, {addr.line2}</>}
          <br />
          {[addr.city, addr.state, addr.zip].filter(Boolean).join(", ")}
          {addr.country && <>, {addr.country}</>}
        </div>
      )}

      {/* Tracking info (shipped + complete) */}
      {order.trackingNumber && (
        <div style={{ background: "rgba(74,164,225,0.12)", border: "1px solid rgba(74,164,225,0.4)", borderRadius: "8px", padding: "0.55rem 0.75rem", fontSize: "0.8rem", color: B.sky }}>
          🚚 <strong>{(order.trackingCarrier || "").toUpperCase()}</strong> · <code style={{ color: "white" }}>{order.trackingNumber}</code>
          {order.shippedAt && <span style={{ color: B.muted, marginLeft: "0.5rem" }}>({formatTimestamp(order.shippedAt)})</span>}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        {status === "pending" && (
          <button onClick={() => wrap(onMarkPacked)} disabled={busy} style={primaryBtn(B.gold, B.dark)}>
            {busy ? "…" : "✅ Mark as Packed"}
          </button>
        )}
        {status === "packed" && (
          <button onClick={() => onOpenShipModal(order)} style={primaryBtn(B.sky, "white")}>
            🚚 Enter tracking & Ship
          </button>
        )}
        {status === "shipped" && (
          <button onClick={() => wrap(onMarkComplete)} disabled={busy} style={primaryBtn(B.green, "white")}>
            {busy ? "…" : "🎉 Mark as Complete"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Tracking entry modal ──────────────────────────────────────────────────

function TrackingModal({ order, onClose, onConfirm }) {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingCarrier, setTrackingCarrier] = useState("usps");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    if (!trackingNumber.trim()) { setError("Enter a tracking number."); return; }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({ trackingNumber, trackingCarrier });
    } catch (err) {
      setError(err.message || "Failed to save.");
      setSubmitting(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", zIndex: 200 }} onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{ background: B.panel, border: `1px solid ${B.gold}`, borderRadius: "14px", padding: "1.25rem", maxWidth: "400px", width: "100%", color: "white", fontFamily: FONT.body }}
      >
        <div style={{ fontFamily: FONT.display, color: B.gold, fontWeight: 800, fontSize: "1.05rem", marginBottom: "0.5rem" }}>
          🚚 Ship order
        </div>
        <div style={{ color: B.muted, fontSize: "0.82rem", marginBottom: "1rem" }}>
          Enter tracking info. The customer gets an email with the number the moment you save.
        </div>

        <label style={{ display: "block", marginBottom: "0.9rem" }}>
          <div style={labelStyle}>Tracking number</div>
          <input
            autoFocus
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="9400 1000 0000 0000 0000 00"
            style={inputStyle}
          />
        </label>

        <label style={{ display: "block", marginBottom: "1rem" }}>
          <div style={labelStyle}>Carrier</div>
          <select value={trackingCarrier} onChange={(e) => setTrackingCarrier(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
            {CARRIERS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>

        {error && (
          <div style={{ background: "rgba(239,83,126,0.15)", border: "1px solid rgba(239,83,126,0.45)", color: "#ffb8c8", padding: "0.55rem 0.75rem", borderRadius: "8px", fontSize: "0.8rem", marginBottom: "0.9rem" }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} disabled={submitting} style={{ background: "transparent", border: `1px solid ${B.border}`, color: B.muted, borderRadius: "8px", padding: "0.55rem 1rem", fontSize: "0.88rem", fontWeight: 600, cursor: "pointer" }}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} style={primaryBtn(B.gold, B.dark)}>
            {submitting ? "Sending…" : "Ship + notify customer"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Helpers ──

function Loading() {
  return <div style={{ color: B.muted, fontSize: "0.95rem", textAlign: "center", padding: "3rem 1rem" }}>Loading shop orders…</div>;
}

function EmptyState() {
  return (
    <div style={{ background: B.panel, border: `1px dashed ${B.border}`, borderRadius: "12px", padding: "2.5rem 1rem", textAlign: "center", color: B.muted }}>
      <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>📦</div>
      <div style={{ color: B.cream, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.35rem" }}>No shop orders yet</div>
      <div style={{ fontSize: "0.88rem", maxWidth: "360px", margin: "0 auto", lineHeight: 1.5 }}>
        Orders placed through <code>/shop</code> land here automatically once Square confirms payment.
      </div>
    </div>
  );
}

function formatCents(cents) {
  if (cents == null || isNaN(cents)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const labelStyle = {
  color: B.muted, fontSize: "0.72rem", fontWeight: 700,
  letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "0.3rem",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: B.card, border: `1px solid ${B.border}`,
  color: "white", fontSize: "0.92rem", padding: "0.55rem 0.75rem",
  borderRadius: "8px", outline: "none", fontFamily: "inherit",
};
function primaryBtn(bg, color) {
  return { background: bg, color, border: "none", borderRadius: "8px", padding: "0.55rem 1rem", fontSize: "0.88rem", fontWeight: 800, fontFamily: FONT.display, cursor: "pointer", whiteSpace: "nowrap" };
}
